// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useRef } from "react";
import { weekdayNames, monthName } from "@almanac/i18n";
import type { CalendarMonth, Cents, IsoDate } from "@almanac/domain";
import { useI18n } from "../lib/i18n.js";
import { moveDate } from "./calendarKeys.js";

interface Props {
  months: CalendarMonth[];
  year: number;
  selected: IsoDate;
  cap: Cents;
  onSelect: (date: IsoDate) => void;
  describedBy: string;
}

/**
 * The flat calendar: twelve month tables forming one keyboard grid. Only the
 * selected day is in the tab order (a roving tabindex), so Tab moves past
 * the calendar in one step and arrow keys move within it.
 */
export function CalendarGrid({ months, year, selected, cap, onSelect, describedBy }: Props) {
  const { locale, money, date, t } = useI18n();
  const container = useRef<HTMLDivElement>(null);
  const focusOnChange = useRef(false);
  const weekdays = weekdayNames(locale);

  useEffect(() => {
    if (!focusOnChange.current) return;
    focusOnChange.current = false;
    container.current?.querySelector<HTMLElement>(`[data-date="${selected}"]`)?.focus();
  }, [selected]);

  return (
    <div className="year-grid" ref={container}>
      {months.map((m) => {
        const rows: (typeof m.cells)[] = Array.from({ length: m.rows }, () => []);
        for (const cell of m.cells) rows[cell.row]!.push(cell);
        return (
          <table className="month-grid" role="grid" key={m.month} aria-describedby={describedBy}>
            <caption>{monthName(m.month, locale)}</caption>
            <thead>
              <tr>
                {weekdays.map((w) => (
                  <th scope="col" key={w} abbr={w}>
                    {w.slice(0, 1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, r) => (
                <tr key={r}>
                  {Array.from({ length: 7 }, (_, col) => {
                    const cell = cells.find((c) => c.column === col);
                    if (!cell) return <td key={col} aria-hidden="true" style={{ background: "transparent", cursor: "default" }} />;
                    const strength = Math.min(1, Math.abs(cell.value) / cap);
                    const color = cell.value >= 0 ? "var(--positive)" : "var(--negative)";
                    const isSelected = cell.date === selected;
                    return (
                      <td
                        key={col}
                        role="gridcell"
                        data-date={cell.date}
                        tabIndex={isSelected ? 0 : -1}
                        aria-selected={isSelected}
                        style={{
                          background: cell.value === 0 ? undefined : `color-mix(in srgb, ${color} ${Math.round(20 + strength * 70)}%, var(--surface-2))`,
                          color: strength > 0.6 ? "#fff" : undefined,
                        }}
                        onClick={() => onSelect(cell.date)}
                        onKeyDown={(e) => {
                          const next = moveDate(cell.date, e.key, year);
                          if (next) {
                            e.preventDefault();
                            focusOnChange.current = true;
                            onSelect(next);
                          }
                        }}
                      >
                        <span aria-hidden="true">{Number(cell.date.slice(8, 10))}</span>
                        <span className="visually-hidden">
                          {t("calendar.selected", {
                            date: date(cell.date, "long"),
                            value: cell.value === 0 ? t("calendar.noTransactions") : money(cell.value),
                          })}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        );
      })}
    </div>
  );
}
